<template>
  <div class="modal-content factura-reajuste-modal">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold">{{ props.label }}</label>
      <DxNumberBox v-bind="{...props.dxInputProps, step: 1}" v-model="reajusteValue" ref="dxEditorRef">
        <DxValidator ref="dxValidatorRef">
          <DxRequiredRule v-if="props.required" :message="$t('shared.input_box.campo_requerido')" />
          <DxCustomRule
            :validation-callback="validarReajusteTotalEfectivo"
            :message="mensajeValidacionTotalEfectivo"
            :reevaluate="true"
          />
        </DxValidator>
      </DxNumberBox>
    </div>

    <div class="desglose-panel rounded border bg-light p-3">
      <div class="desglose-row d-flex justify-content-between align-items-center mb-2">
        <span class="text-muted">{{ $t("facturas.reajuste_modal.importe_base") }}</span>
        <span class="fw-semibold">{{ formatImporte(importeBaseMostrado) }}</span>
      </div>
      <div class="desglose-row d-flex justify-content-between align-items-center mb-2">
        <span class="text-muted">{{ $t("facturas.reajuste_modal.importe_reajuste") }}</span>
        <span class="fw-semibold">{{ formatImporte(importeReajusteMostrado) }}</span>
      </div>
      <hr class="my-2" />
      <div class="desglose-row d-flex justify-content-between align-items-center">
        <span class="fw-semibold">{{ $t("facturas.reajuste_modal.importe_total_efectivo") }}</span>
        <span class="fw-bold text-primary">{{ formatImporte(importeTotalEfectivoPreview) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { modalDefinedEmits, type ToolbarReference, useModalBuilder } from "@helpers/modal-building-helper";
import { messageBoxResult } from "@components/dialogs/message-box";
import { DxButton } from "devextreme-vue/button";
import DxNumberBox from "devextreme-vue/number-box";
import { DxValidator, DxRequiredRule, DxCustomRule } from "devextreme-vue/validator";
import { useIntl } from "@services/i18n";
import { importeRetribucionParaMostrar } from "@helpers/retribucion-economica-helper";
import type { IFacturaReajusteModalProps } from "./models/IFacturaReajusteModalProps";

const props = withDefaults(defineProps<IFacturaReajusteModalProps>(), {
  required: true,
  esRetrocomision: false,
  importeReajuste: null,
});

defineEmits([...modalDefinedEmits]);

const intl = useIntl();

const reajusteValue = ref<number | null>(props.importeReajuste ?? (props.dxInputProps?.value as number | null) ?? null);
const dxEditorRef = ref<DxNumberBox>(); 
const dxValidatorRef = ref<DxValidator>();

function redondearImporte(v: number): number {
  return Math.round(v * 100) / 100;
}

function importeMostrado(importe: number | null | undefined): number | null | undefined {
  return importeRetribucionParaMostrar(importe, props.esRetrocomision);
}

const importeBaseMostrado = computed(() => importeMostrado(props.importeBase));
const importeReajusteMostrado = computed(() => importeMostrado(reajusteValue.value));

const importeTotalEfectivoPreview = computed(() =>
  importeMostrado(redondearImporte((props.importeBase ?? 0) + (reajusteValue.value ?? 0))),
);

const mensajeValidacionTotalEfectivo = computed(() =>
  props.esRetrocomision
    ? intl.i18n.t("facturas.reajuste_modal.validacion_total_positivo_retrocomision")
    : intl.i18n.t("facturas.reajuste_modal.validacion_total_negativo_comision"),
);

function totalEfectivoMostrado(reajuste: number): number {
  const totalAlmacenado = redondearImporte((props.importeBase ?? 0) + reajuste);
  return importeRetribucionParaMostrar(totalAlmacenado, props.esRetrocomision) ?? 0;
}

function validarReajusteTotalEfectivo(params: { value: unknown }): boolean {
  const reajuste = params.value as number | null | undefined;
  if (reajuste == null || Number.isNaN(Number(reajuste))) {
    return true;
  }

  const totalMostrado = totalEfectivoMostrado(Number(reajuste));

  if (props.esRetrocomision) {
    return totalMostrado <= 0;
  }

  return totalMostrado >= 0;
}

function formatImporte(value: number | null | undefined): string {
  const v = value ?? 0;
  return `${intl.i18n.n(Number(v.toFixed(2)), "decimal")} €`;
}

onMounted(() => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);
  configureButtons(modal);
});

const configureButtons = (modal: ReturnType<typeof useModalBuilder>) => {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;

  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());

  okButton.update((w) => (w.onClick = () => {
    const validationResult = dxValidatorRef.value!.instance!.validate();
    if (validationResult.isValid) {
      modal.close({ result: messageBoxResult.ok, value: reajusteValue.value });
    } else {
      dxValidatorRef.value!.instance!.focus();
    }
  }));

  cancelButton.update((w) => (w.onClick = () => modal.close({ result: messageBoxResult.cancel })));
};
</script>

<style scoped lang="scss">
.factura-reajuste-modal {
  .desglose-panel {
    border-color: var(--bs-border-color, #dee2e6) !important;
  }
}
</style>
